import React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Link } from "react-router-dom";
import DownloadLink from "./download-link";
import { omitProps } from "./omit-props";

// Helper to render text with <br> tags converted to actual line breaks
const renderTextWithBreaks = (text: string): React.ReactNode => {
  if (!text.includes("<br")) return text;

  const parts = text.split(/<br\s*\/?>/gi);
  return parts.map((part, index) => (
    <React.Fragment key={index}>
      {part}
      {index < parts.length - 1 && <br />}
    </React.Fragment>
  ));
};

interface MarkdownRendererProps {
  content: string;
  className?: string;
  // Lets a caller keep one stable test hook across a markdown/plain-text fallback pair.
  "data-test"?: string;
  // Fetches and saves a /downloads/:id file in place; without it those links navigate.
  onDownloadAttachment?: (attachmentId: string) => Promise<void>;
}

/**
 * Extracts the attachment ID from a download URL.
 * Matches URLs like /downloads/:attachmentId or full URLs ending with /downloads/:attachmentId
 * @returns The attachment ID if the URL is a download link, null otherwise
 */
const extractDownloadAttachmentId = (href: string): string | null => {
  // Match both relative and absolute URLs
  // Pattern: /downloads/:attachmentId (where attachmentId is a UUID or similar)
  const downloadPattern = /\/downloads\/([a-zA-Z0-9-]+)(?:\?.*)?(?:#.*)?$/;
  const match = href.match(downloadPattern);
  return match ? match[1] : null;
};

const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({
  content,
  className = "",
  "data-test": dataTest,
  onDownloadAttachment
}) => {
  // Process content:
  // 1. Convert escaped newlines to actual newlines
  // 2. Ensure a blank line after the last list item when followed by non-list content
  //    (prevents Markdown lazy continuation from absorbing trailing text into the list)
  // 3. Collapse 3+ consecutive newlines to just 2 (preserves paragraph breaks but removes excessive whitespace)
  const processedContent = content
    .replace(/\\n/g, "\n")
    .replace(
      /^([ \t]*(?:[-*+]|\d+[.)]) .+)\n((?![ \t]*(?:[-*+]|\d+[.)]) |\s*$).)/gm,
      "$1\n\n$2"
    )
    .replace(/\n{3,}/g, "\n\n");
  return (
    <div
      className={`${className} overflow-hidden whitespace-normal`}
      data-test={dataTest}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          // Code block
          code: ({ className, children, ...props }) => {
            const match = /language-(\w+)/.exec(className || "");
            const isCodeBlock =
              match && typeof children === "string" && children.includes("\n");

            if (!isCodeBlock) {
              return (
                <code
                  className="bg-surface-elevation1 px-1 py-0.5 rounded text-base font-mono"
                  {...props}
                >
                  {children}
                </code>
              );
            }
            return (
              <div className="relative my-4 overflow-hidden rounded-md">
                {match && (
                  <div className="absolute top-0 right-0 px-2 py-1 text-sm font-semibold text-text-code-language bg-surface-code-language rounded-bl-md">
                    {match[1]}
                  </div>
                )}
                <pre className="p-4 pt-6 overflow-x-auto bg-surface-elevation1 text-base font-mono">
                  <code {...props}>
                    {typeof children === "string"
                      ? children.replace(/\n$/, "")
                      : children}
                  </code>
                </pre>
              </div>
            );
          },
          // Links
          a(allProps) {
            const { href, children, ...props } = omitProps(allProps, "node");
            if (!href) return null;

            // Check if this is a download link
            const attachmentId = extractDownloadAttachmentId(href);
            if (attachmentId) {
              return (
                <DownloadLink
                  attachmentId={attachmentId}
                  href={href}
                  onDownload={onDownloadAttachment}
                >
                  {children}
                </DownloadLink>
              );
            }

            if (href.startsWith("/") && !href.startsWith("//")) {
              return (
                <Link
                  to={href}
                  className="text-link hover:underline"
                  {...props}
                >
                  {children}
                </Link>
              );
            }

            return (
              <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="text-link hover:underline"
                {...props}
              >
                {children}
              </a>
            );
          },
          // Images
          img(allProps) {
            const { src, alt, ...props } = omitProps(allProps, "node");
            if (!src) return null;
            return (
              <div className="relative my-4">
                <img
                  src={src}
                  alt={alt || "Image"}
                  className="max-w-full h-auto rounded-lg shadow-sm"
                  onError={(e) => {
                    // Replace with error UI if image fails to load
                    const target = e.target as HTMLImageElement;
                    target.onerror = null;
                    target.classList.add("hidden");
                    const parent = target.parentNode as HTMLElement;
                    const errorDiv = document.createElement("div");
                    errorDiv.className =
                      "text-error p-3 rounded-lg text-center";
                    errorDiv.textContent = "Failed to load image";
                    parent.appendChild(errorDiv);
                  }}
                  {...props}
                />
              </div>
            );
          },
          // Tables
          table(allProps) {
            const { children, ...props } = omitProps(allProps, "node");
            return (
              <div className="my-4 max-w-full overflow-auto">
                <div className="rounded-lg border border-border-widget-card bg-surface-elevation2 w-fit">
                  <table
                    className="divide-y divide-border-widget-card rounded-md"
                    {...props}
                  >
                    {children}
                  </table>
                </div>
              </div>
            );
          },
          thead(allProps) {
            const { children, ...props } = omitProps(allProps, "node");
            return (
              <thead className="text-text-table-head" {...props}>
                {children}
              </thead>
            );
          },
          tbody(allProps) {
            const { children, ...props } = omitProps(allProps, "node");
            return (
              <tbody className="divide-y divide-border-widget-card" {...props}>
                {children}
              </tbody>
            );
          },
          th(allProps) {
            const { children, ...props } = omitProps(allProps, "node");
            return (
              <th
                scope="col"
                className="px-6 py-3 text-left text-sm font-medium text-text-table-header-cell tracking-wider"
                {...props}
              >
                {children}
              </th>
            );
          },
          td(allProps) {
            const { children, ...props } = omitProps(allProps, "node");
            // Process children to handle <br> tags in text
            const processedChildren = React.Children.map(children, (child) => {
              if (typeof child === "string") {
                return renderTextWithBreaks(child);
              }
              return child;
            });
            return (
              <td
                className="px-6 py-4 whitespace-normal text-base align-top text-left"
                {...props}
              >
                {processedChildren}
              </td>
            );
          },
          // Paragraphs
          p(allProps) {
            const { children, ...props } = omitProps(allProps, "node");
            return (
              <p
                className="mt-0.5 mb-1 text-base font-normal leading-5 text-primary last:mb-1"
                {...props}
              >
                {children}
              </p>
            );
          },
          // Headings
          h1(allProps) {
            const { children, ...props } = omitProps(allProps, "node");
            return (
              <h1 className="text-2xl font-bold m-1" {...props}>
                {children}
              </h1>
            );
          },
          h2(allProps) {
            const { children, ...props } = omitProps(allProps, "node");
            return (
              <h2 className="text-xl font-bold m-1" {...props}>
                {children}
              </h2>
            );
          },
          h3(allProps) {
            const { children, ...props } = omitProps(allProps, "node");
            return (
              <h3 className="text-lg font-bold m-1" {...props}>
                {children}
              </h3>
            );
          },
          h4(allProps) {
            const { children, ...props } = omitProps(allProps, "node");
            return (
              <h4
                className="text-base font-normal leading-5 text-secondary my-1"
                {...props}
              >
                {children}
              </h4>
            );
          },
          // Lists
          ul(allProps) {
            const { children, ...props } = omitProps(allProps, "node");
            return (
              <ul className="list-disc pl-6 mb-1" {...props}>
                {children}
              </ul>
            );
          },

          ol(allProps) {
            const { children, ...props } = omitProps(allProps, "node");
            return (
              <ol className="list-decimal pl-6 mb-1" {...props}>
                {children}
              </ol>
            );
          },

          li(allProps) {
            const { children, ...props } = omitProps(allProps, "node");
            return (
              <li className="mb-1" {...props}>
                {children}
              </li>
            );
          },
          // horizontal rule
          hr(allProps) {
            const props = omitProps(allProps, "node");
            return <hr className="my-2 border-t" {...props} />;
          },
          // Block quotes
          blockquote(allProps) {
            const { children, ...props } = omitProps(allProps, "node");
            return (
              <blockquote
                className="border-l-4 border-border-blockquote pl-4 py-1 my-4 italic"
                {...props}
              >
                {children}
              </blockquote>
            );
          }
        }}
      >
        {processedContent}
      </ReactMarkdown>
    </div>
  );
};

export default MarkdownRenderer;
